import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  ConfirmModal,
  SuccessModal,
  ErrorModal,
} from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Search,
  Email,
  Send,
  History,
  People,
  FilterList,
  CheckCircle,
  Error,
  Warning,
  Close,
  Visibility,
  Add,
  Campaign,
  PersonSearch,
  Group,
  Schedule,
  TrendingUp
} from '../../mui-icons';
import './NotificationsPage.css';
import PushCampaignPanel from './PushCampaignPanel';
import { PhoneIphone } from '../../mui-icons';

const NotificationsPage = () => {
  const { canEdit } = useAdminAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('email-compose'); // email-compose | email-history | push
  
  // Filters state
  const [availableFilters, setAvailableFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  
  // Recipients state
  const [recipientType, setRecipientType] = useState('all_active');
  const [selectedFilter, setSelectedFilter] = useState('subscribers');
  const [selectedUserIds, setSelectedUserIds] = useState('');
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  
  // Email form state
  const [emailForm, setEmailForm] = useState({
    subject: '',
    title: '',
    body: '',
    cta_text: '',
    cta_link: '',
    personalize: true
  });
  
  // Send state
  const [sending, setSending] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [resultModal, setResultModal] = useState({ open: false, data: null });
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0
  });
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  // Load filters on mount
  useEffect(() => {
    loadFilters();
  }, []);

  // Load history when tab changes
  useEffect(() => {
    if (activeTab === 'email-history') {
      loadHistory();
    }
  }, [activeTab, historyPagination.page]);

  const loadFilters = async () => {
    setFiltersLoading(true);
    try {
      const response = await adminAPI.getNotificationFilters();
      if (response.data?.filters) {
        setAvailableFilters(response.data.filters);
      }
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = {
        page: historyPagination.page,
        limit: historyPagination.limit
      };
      if (historyDateFrom) params.date_from = historyDateFrom;
      if (historyDateTo) params.date_to = historyDateTo;
      
      const response = await adminAPI.getNotificationHistory(params);
      if (response.data) {
        setHistory(response.data.campaigns || []);
        if (response.data.pagination) {
          setHistoryPagination(prev => ({
            ...prev,
            total: response.data.pagination.total,
            total_pages: response.data.pagination.total_pages
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getRecipientsConfig = useCallback(() => {
    if (recipientType === 'all_active') {
      return { type: 'all_active' };
    } else if (recipientType === 'filter') {
      return { type: 'filter', filter: selectedFilter };
    } else if (recipientType === 'selected') {
      const ids = selectedUserIds
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id) && id > 0);
      return { type: 'selected', user_ids: ids };
    }
    return { type: 'all_active' };
  }, [recipientType, selectedFilter, selectedUserIds]);

  const countRecipients = async () => {
    setCountLoading(true);
    setRecipientCount(null);
    try {
      const recipients = getRecipientsConfig();
      const response = await adminAPI.countNotificationRecipients(recipients);
      if (response.data) {
        setRecipientCount({
          total: response.data.total_recipients,
          description: response.data.description
        });
      }
    } catch (error) {
      console.error('Erro ao contar destinatários:', error);
      setRecipientCount({ total: 0, error: error.message || 'Erro ao contar' });
    } finally {
      setCountLoading(false);
    }
  };

  const validateForm = () => {
    if (!emailForm.subject.trim()) return 'Assunto é obrigatório';
    if (!emailForm.title.trim()) return 'Título é obrigatório';
    if (!emailForm.body.trim()) return 'Corpo do email é obrigatório';
    if (emailForm.cta_text && !emailForm.cta_link) return 'Link do botão é obrigatório quando há texto';
    if (emailForm.cta_link && !emailForm.cta_text) return 'Texto do botão é obrigatório quando há link';
    if (recipientType === 'selected') {
      const ids = selectedUserIds.split(',').filter(id => id.trim());
      if (ids.length === 0) return 'Informe ao menos um ID de usuário';
    }
    return null;
  };

  const handleSendClick = () => {
    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }
    setConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    setConfirmModal(false);
    setSending(true);
    
    try {
      const data = {
        subject: emailForm.subject.trim(),
        title: emailForm.title.trim(),
        body: emailForm.body.trim(),
        recipients: getRecipientsConfig(),
        personalize: emailForm.personalize
      };
      
      if (emailForm.cta_text && emailForm.cta_link) {
        data.cta_text = emailForm.cta_text.trim();
        data.cta_link = emailForm.cta_link.trim();
      }
      
      console.log('=== DEBUG sendNotificationEmail ===');
      console.log('Request Data:', JSON.stringify(data, null, 2));
      
      const response = await adminAPI.sendNotificationEmail(data);
      
      console.log('Response:', response);
      
      setResultModal({
        open: true,
        data: {
          success: true,
          total: response.data?.total_recipients || 0,
          sent: response.data?.sent_count || 0,
          failed: response.data?.failed_count || 0,
          errors: response.data?.errors || []
        }
      });
      
      // Limpar formulário após sucesso
      setEmailForm({
        subject: '',
        title: '',
        body: '',
        cta_text: '',
        cta_link: '',
        personalize: true
      });
      setRecipientCount(null);
      
    } catch (error) {
      console.error('=== ERRO sendNotificationEmail ===');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      setResultModal({
        open: true,
        data: {
          success: false,
          error: error.message || 'Erro ao enviar notificação'
        }
      });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilterLabel = (filterValue) => {
    const filter = availableFilters.find(f => f.filter === filterValue);
    return filter?.label || filterValue;
  };

  return (
    <div className="notifications-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <Email sx={{ fontSize: 32 }} />
          <div>
            <h1>Notificações</h1>
            <p>Email marketing e campanhas push no app</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-btn ${activeTab === 'email-compose' ? 'active' : ''}`}
          onClick={() => setActiveTab('email-compose')}
        >
          <Send sx={{ fontSize: 20 }} />
          Email
        </button>
        <button 
          className={`tab-btn ${activeTab === 'email-history' ? 'active' : ''}`}
          onClick={() => setActiveTab('email-history')}
        >
          <History sx={{ fontSize: 20 }} />
          Histórico Email
        </button>
        <button 
          className={`tab-btn ${activeTab === 'push' ? 'active' : ''}`}
          onClick={() => setActiveTab('push')}
        >
          <PhoneIphone sx={{ fontSize: 20 }} />
          Push App
        </button>
      </div>

      {/* ==================== COMPOSE TAB ==================== */}
      {activeTab === 'push' && (
        <PushCampaignPanel canEdit={canEdit} />
      )}

      {activeTab === 'email-compose' && (
        <div className="tab-content compose-content">
          <div className="compose-grid">
            {/* Recipients Section */}
            <div className="admin-card pn-card pn-card--default recipients-card">
              <div className="card-header">
                <People sx={{ fontSize: 24 }} />
                <h3>Destinatários</h3>
              </div>
              
              <div className="recipients-options">
                {/* All Active */}
                <label className={`recipient-option ${recipientType === 'all_active' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="recipientType"
                    value="all_active"
                    checked={recipientType === 'all_active'}
                    onChange={(e) => setRecipientType(e.target.value)}
                  />
                  <div className="option-content">
                    <Group sx={{ fontSize: 20 }} />
                    <div>
                      <strong>Todos os usuários ativos</strong>
                      <span>Usuários com email verificado</span>
                    </div>
                  </div>
                </label>

                {/* Filter */}
                <label className={`recipient-option ${recipientType === 'filter' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="recipientType"
                    value="filter"
                    checked={recipientType === 'filter'}
                    onChange={(e) => setRecipientType(e.target.value)}
                  />
                  <div className="option-content">
                    <FilterList sx={{ fontSize: 20 }} />
                    <div>
                      <strong>Filtrar por segmento</strong>
                      <span>Selecione um grupo específico</span>
                    </div>
                  </div>
                </label>
                
                {recipientType === 'filter' && (
                  <div className="filter-select-wrapper">
                    <select
                      className="admin-input"
                      value={selectedFilter}
                      onChange={(e) => setSelectedFilter(e.target.value)}
                    >
                      {availableFilters
                        .filter(f => f.type === 'filter')
                        .map(f => (
                          <option key={f.filter} value={f.filter}>
                            {f.label}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                )}

                {/* Selected IDs */}
                <label className={`recipient-option ${recipientType === 'selected' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="recipientType"
                    value="selected"
                    checked={recipientType === 'selected'}
                    onChange={(e) => setRecipientType(e.target.value)}
                  />
                  <div className="option-content">
                    <PersonSearch sx={{ fontSize: 20 }} />
                    <div>
                      <strong>Selecionar por IDs</strong>
                      <span>Informe IDs específicos</span>
                    </div>
                  </div>
                </label>
                
                {recipientType === 'selected' && (
                  <div className="ids-input-wrapper">
                    <input
                      type="text"
                      className="admin-input"
                      placeholder="Ex: 1, 5, 10, 25..."
                      value={selectedUserIds}
                      onChange={(e) => setSelectedUserIds(e.target.value)}
                    />
                    <small>Separe os IDs por vírgula</small>
                  </div>
                )}
              </div>

              {/* Count Button */}
              <div className="count-section">
                <Button
                  variant="secondary"
                  className="count-btn"
                  onClick={countRecipients}
                  loading={countLoading}
                >
                  Contar Destinatários
                </Button>
                
                {recipientCount !== null && (
                  <div className={`count-result ${recipientCount.error ? 'error' : ''}`}>
                    {recipientCount.error ? (
                      <>
                        <Error sx={{ fontSize: 20 }} />
                        <span>{recipientCount.error}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 20 }} />
                        <span>
                          <strong>{recipientCount.total.toLocaleString()}</strong> destinatário(s)
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Email Composition Section */}
            <div className="admin-card pn-card pn-card--default compose-card">
              <div className="card-header">
                <Campaign sx={{ fontSize: 24 }} />
                <h3>Composição do Email</h3>
              </div>

              <div className="compose-form">
                <div className="form-group">
                  <label>Assunto do Email *</label>
                  <input
                    type="text"
                    className="admin-input"
                    placeholder="Ex: Novidades PersonalNutri! 🎉"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                    maxLength={200}
                  />
                  <small>{emailForm.subject.length}/200</small>
                </div>

                <div className="form-group">
                  <label>Título Principal *</label>
                  <input
                    type="text"
                    className="admin-input"
                    placeholder="Ex: Lançamento de Nova Funcionalidade"
                    value={emailForm.title}
                    onChange={(e) => setEmailForm({ ...emailForm, title: e.target.value })}
                    maxLength={200}
                  />
                  <small>{emailForm.title.length}/200</small>
                </div>

                <div className="form-group">
                  <label>Corpo da Mensagem *</label>
                  <textarea
                    className="admin-input"
                    placeholder="Escreva sua mensagem aqui...&#10;&#10;Use linhas em branco para separar parágrafos."
                    rows={8}
                    value={emailForm.body}
                    onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  />
                  <small>Use linhas em branco para separar parágrafos</small>
                </div>

                <div className="form-section">
                  <h4>Botão de Ação (opcional)</h4>
                  <div className="cta-row">
                    <div className="form-group">
                      <label>Texto do Botão</label>
                      <input
                        type="text"
                        className="admin-input"
                        placeholder="Ex: Conferir Agora"
                        value={emailForm.cta_text}
                        onChange={(e) => setEmailForm({ ...emailForm, cta_text: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Link do Botão</label>
                      <input
                        type="url"
                        className="admin-input"
                        placeholder="https://..."
                        value={emailForm.cta_link}
                        onChange={(e) => setEmailForm({ ...emailForm, cta_link: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={emailForm.personalize}
                      onChange={(e) => setEmailForm({ ...emailForm, personalize: e.target.checked })}
                    />
                    <span>Personalizar com nome do usuário na saudação</span>
                  </label>
                </div>

                {/* Send Button */}
                {canEdit() && (
                  <Button
                    variant="primary"
                    className="send-btn"
                    onClick={handleSendClick}
                    loading={sending}
                    disabled={!emailForm.subject || !emailForm.title || !emailForm.body}
                  >
                    <Send sx={{ fontSize: 18 }} />
                    {recipientCount?.total ? `Enviar para ${recipientCount.total.toLocaleString()} usuário(s)` : 'Enviar Email'}
                  </Button>
                )}

                {!canEdit() && (
                  <div className="permission-warning">
                    <Warning sx={{ fontSize: 18 }} />
                    <span>Você não tem permissão para enviar notificações</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Email Preview */}
          <div className="admin-card pn-card pn-card--default preview-card">
            <div className="card-header">
              <Visibility sx={{ fontSize: 24 }} />
              <h3>Pré-visualização</h3>
            </div>
            <div className="email-preview">
              <div className="preview-header">
                <span className="preview-logo">🌱 PersonalNutri</span>
                <span className="preview-tagline">Sua jornada para uma vida mais saudável</span>
              </div>
              <div className="preview-body">
                <h2 className="preview-title">{emailForm.title || 'Título do Email'}</h2>
                {emailForm.personalize && (
                  <p className="preview-greeting">Olá <strong>João</strong>! 👋</p>
                )}
                <div className="preview-content">
                  {emailForm.body ? (
                    emailForm.body.split('\n\n').map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))
                  ) : (
                    <p className="placeholder">Corpo da mensagem aparecerá aqui...</p>
                  )}
                </div>
                {emailForm.cta_text && (
                  <button className="preview-cta">{emailForm.cta_text}</button>
                )}
              </div>
              <div className="preview-footer">
                <p>Equipe PersonalNutri</p>
                <p>contato@personalnutri.app</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== HISTORY TAB ==================== */}
      {activeTab === 'email-history' && (
        <div className="tab-content history-content">
          {/* Filters */}
          <div className="admin-card pn-card pn-card--default filters-card">
            <div className="filters-row">
              <div className="filter-group">
                <label>Data Inicial</label>
                <input
                  type="date"
                  className="admin-input"
                  value={historyDateFrom}
                  onChange={(e) => setHistoryDateFrom(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label>Data Final</label>
                <input
                  type="date"
                  className="admin-input"
                  value={historyDateTo}
                  onChange={(e) => setHistoryDateTo(e.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                onClick={() => {
                  setHistoryPagination(prev => ({ ...prev, page: 1 }));
                  loadHistory();
                }}
              >
                <Search sx={{ fontSize: 18 }} />
                Filtrar
              </Button>
            </div>
          </div>

          {/* History Table */}
          <div className="admin-card pn-card pn-card--default">
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Assunto</th>
                    <th>Enviados</th>
                    <th>Sucesso</th>
                    <th>Falhas</th>
                    <th>Enviado por</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td colSpan="6" className="loading-cell">
                        <Skeleton height={40} />
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="empty-cell">
                        <History sx={{ fontSize: 48, opacity: 0.3 }} />
                        <p>Nenhuma campanha encontrada</p>
                      </td>
                    </tr>
                  ) : (
                    history.map((campaign, index) => (
                      <tr key={index}>
                        <td>
                          <span className="date-cell">
                            <Schedule sx={{ fontSize: 16 }} />
                            {formatDate(campaign.campaign_time)}
                          </span>
                        </td>
                        <td>
                          <strong>{campaign.subject}</strong>
                        </td>
                        <td>
                          <span className="stat-badge total">
                            {campaign.total_sent?.toLocaleString() || 0}
                          </span>
                        </td>
                        <td>
                          <span className="stat-badge success">
                            <CheckCircle sx={{ fontSize: 14 }} />
                            {campaign.success_count?.toLocaleString() || 0}
                          </span>
                        </td>
                        <td>
                          <span className={`stat-badge ${campaign.failed_count > 0 ? 'error' : 'neutral'}`}>
                            {campaign.failed_count > 0 && <Error sx={{ fontSize: 14 }} />}
                            {campaign.failed_count?.toLocaleString() || 0}
                          </span>
                        </td>
                        <td>
                          <span className="admin-email">{campaign.sent_by || '-'}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {historyPagination.total_pages > 1 && (
              <div className="pagination">
                <Button
                  variant="secondary"
                  disabled={historyPagination.page <= 1}
                  onClick={() => setHistoryPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <span className="pagination-info">
                  Página {historyPagination.page} de {historyPagination.total_pages}
                </span>
                <Button
                  variant="secondary"
                  disabled={historyPagination.page >= historyPagination.total_pages}
                  onClick={() => setHistoryPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <FormModal
        open={confirmModal}
        onClose={() => setConfirmModal(false)}
        title="Confirmar Envio"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleConfirmSend}>
              <Send sx={{ fontSize: 18 }} />
              Confirmar Envio
            </Button>
          </>
        }
      >
        <p>
          Você está prestes a enviar um email para{' '}
          <strong>{recipientCount?.total?.toLocaleString() || 'N/A'}</strong> destinatário(s).
        </p>
        <p><strong>Assunto:</strong> {emailForm.subject}</p>
        <p><strong>Título:</strong> {emailForm.title}</p>
        <p>Esta ação não pode ser desfeita.</p>
      </FormModal>

      <FormModal
        open={resultModal.open && resultModal.data?.success}
        onClose={() => setResultModal({ open: false, data: null })}
        title="Envio Concluído"
        footer={
          <Button variant="primary" onClick={() => setResultModal({ open: false, data: null })}>
            Fechar
          </Button>
        }
      >
        <div className="result-stats">
          <div className="stat-item">
            <span className="stat-value">{resultModal.data?.total?.toLocaleString()}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item success">
            <span className="stat-value">{resultModal.data?.sent?.toLocaleString()}</span>
            <span className="stat-label">Enviados</span>
          </div>
          <div className="stat-item error">
            <span className="stat-value">{resultModal.data?.failed?.toLocaleString()}</span>
            <span className="stat-label">Falhas</span>
          </div>
        </div>
        {resultModal.data?.errors?.length > 0 && (
          <div className="result-errors">
            <h4>Erros ({resultModal.data.errors.length}):</h4>
            <ul>
              {resultModal.data.errors.slice(0, 5).map((err, i) => (
                <li key={i}>
                  <strong>{err.email}:</strong> {err.error}
                </li>
              ))}
              {resultModal.data.errors.length > 5 && (
                <li>... e mais {resultModal.data.errors.length - 5} erros</li>
              )}
            </ul>
          </div>
        )}
      </FormModal>

      <ErrorModal
        open={resultModal.open && resultModal.data && !resultModal.data.success}
        title="Erro no Envio"
        message={resultModal.data?.error || 'Ocorreu um erro ao enviar as notificações.'}
        onClose={() => setResultModal({ open: false, data: null })}
      />
    </div>
  );
};

export default NotificationsPage;

